import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createOrGetAutobookRule,
  deleteAutobookRule,
  getAutobookRules,
  patchAutobookRule,
  SaveApiResMessage,
} from '@/actions';
import { Icons } from '@/assets';
import { Icon } from '@/components/Icon';
import LoadingV2 from '@/components/Loading';
import { TableV3 } from '@/components/TableV3';
import IconButton from '@/ctComponents/IconButton';
import Modal from '@/ctComponents/Modal';
import { AutobookRuleFormBody } from '@/components/AutobookRules';

const FALLBACK_DELIVERY_TYPE_OPTIONS = [
  { label: 'All Delivery Types', value: 'ALL' },
  { label: 'Live Unload', value: 'LIVE_UNLOAD' },
  { label: 'Drop & Pick', value: 'DROP_PICK' },
];

/**
 * آبجکت deliveryTypes از پاسخ create-or-get / هر سطح data
 */
function getDeliveryTypesObjectFromResponse(inner, body) {
  const candidates = [
    inner?.deliveryTypes,
    inner?.delivery_types,
    body?.data?.deliveryTypes,
    body?.data?.delivery_types,
    body?.deliveryTypes,
    body?.delivery_types,
  ];
  for (const c of candidates) {
    if (c && typeof c === 'object' && !Array.isArray(c)) {
      const keys = Object.keys(c);
      if (keys.length > 0) return c;
    }
  }
  return null;
}

/** لیست select: اگر API چیزی فرستاد همان؛ وگرنه fallback محلی */
function buildDeliveryTypeOptions(apiDeliveryTypes) {
  if (
    apiDeliveryTypes &&
    typeof apiDeliveryTypes === 'object' &&
    !Array.isArray(apiDeliveryTypes)
  ) {
    const entries = Object.entries(apiDeliveryTypes).filter(
      ([k]) => k != null && String(k).trim() !== ''
    );
    if (entries.length > 0) {
      return entries.map(([value, label]) => ({
        value,
        label: label != null && label !== '' ? String(label) : String(value),
      }));
    }
  }
  return FALLBACK_DELIVERY_TYPE_OPTIONS.map((o) => ({ ...o }));
}

function deliveryTypeOptionFromRuleValue(raw, optionsList) {
  const list =
    optionsList?.length > 0 ? optionsList : buildDeliveryTypeOptions(null);
  if (raw == null || raw === '') return list[0];
  const str = String(raw).trim();
  let found = list.find((o) => o.value === str);
  if (found) return found;
  const upper = str.toUpperCase();
  found = list.find((o) => String(o.value).toUpperCase() === upper);
  if (found) return found;
  const lower = str.toLowerCase();
  found = list.find((o) => o.label.toLowerCase() === lower);
  return found ?? list[0];
}

function normalizeDraftCustomers(list) {
  if (!Array.isArray(list)) return [];
  return list.map((c) => ({
    id: String(c.id),
    uuid: c.uuid,
    label: c.name ?? c.label ?? `Customer ${c.id}`,
  }));
}

function normalizeDraftWarehouses(list) {
  if (!Array.isArray(list)) return [];
  return list.map((w) => ({
    id: String(w.id),
    uuid: w.uuid,
    customerId: String(w.customer_id ?? w.customerId ?? ''),
    label: w.name ?? w.label ?? `Warehouse ${w.id}`,
  }));
}

function extractNestedUuids(items) {
  if (!Array.isArray(items)) return [];
  return items
    .map((x) => {
      if (x == null) return '';
      if (typeof x === 'string') return String(x).trim();
      return String(x.uuid ?? x.id ?? '').trim();
    })
    .filter(Boolean);
}

function normalizeDraftWarehouseGroups(list) {
  if (!Array.isArray(list)) return [];
  return list.map((g) => {
    const id = String(g.uuid ?? g.id ?? '');
    const customersNested =
      g.customers ?? g.group_customers ?? g.customer_uuids ?? [];
    const warehousesNested =
      g.warehouses ?? g.group_warehouses ?? g.warehouse_uuids ?? [];
    return {
      id,
      uuid: g.uuid ?? id,
      name: g.name ?? g.group_name ?? 'Warehouse group',
      groupCustomerUuids: extractNestedUuids(customersNested),
      groupWarehouseUuids: extractNestedUuids(warehousesNested),
    };
  });
}

/** کلیدهای تطبیق آیتم rule با کاتالوگ (بر اساس uuid یا id) */
function ruleItemsMatchKeySet(ruleItems) {
  const keys = new Set();
  if (!Array.isArray(ruleItems)) return keys;
  for (const r of ruleItems) {
    if (typeof r === 'string') {
      const t = String(r).trim();
      if (t) {
        keys.add(`u:${t}`);
        keys.add(`i:${t}`);
      }
      continue;
    }
    if (!r || typeof r !== 'object') continue;
    const u = String(
      r.uuid ??
        r.warehouse_group_uuid ??
        r.warehouseGroupUuid ??
        r.group_uuid ??
        r.groupUuid ??
        ''
    ).trim();
    const id = String(r.id ?? r.group_id ?? r.warehouse_group_id ?? '').trim();
    if (u) keys.add(`u:${u}`);
    if (id) keys.add(`i:${id}`);
  }
  return keys;
}

/**
 * آرایهٔ گروه‌های sharing برای Drop & Pick — همان چیزی که با swSharingWarehouseGroups ذخیره می‌شود.
 * ممکن است روی rule یا روی ریشهٔ data (create-or-get) باشد.
 */
function sharingWarehouseGroupsFromApi(inner, rule) {
  const candidates = [
    rule?.swSharingWarehouseGroups,
    rule?.sw_sharing_warehouse_groups,
    rule?.swSharingGroups,
    rule?.sw_sharing_groups,
    inner?.swSharingWarehouseGroups,
    inner?.sw_sharing_warehouse_groups,
    inner?.rule?.swSharingWarehouseGroups,
    inner?.rule?.sw_sharing_warehouse_groups,
  ];
  for (const arr of candidates) {
    if (Array.isArray(arr) && arr.length > 0) return arr;
  }
  for (const arr of candidates) {
    if (Array.isArray(arr)) return arr;
  }
  return [];
}

/** id کاتالوگ (همان فیلد normalized `.id`) برای آیتم‌های انتخاب‌شده در rule */
function catalogIdsMatchingRuleItems(catalog, ruleItems) {
  const keys = ruleItemsMatchKeySet(ruleItems);
  if (keys.size === 0) return [];
  const out = [];
  for (const item of catalog) {
    const u = String(item.uuid ?? '').trim();
    const id = String(item.id ?? '').trim();
    if ((u && keys.has(`u:${u}`)) || (id && keys.has(`i:${id}`))) {
      out.push(String(item.id));
    }
  }
  return out;
}

function catalogUuidsMatchingRuleItems(catalog, ruleItems) {
  const keys = ruleItemsMatchKeySet(ruleItems);
  if (keys.size === 0) return [];
  const out = [];
  for (const item of catalog) {
    const u = String(item.uuid ?? '').trim();
    const id = String(item.id ?? '').trim();
    if ((u && keys.has(`u:${u}`)) || (id && keys.has(`i:${id}`))) {
      if (u) out.push(u);
    }
  }
  return out;
}

function customerUuidsFromWarehouseSelection(
  catalogCustomers,
  catalogWarehouses,
  selectedWarehouseCatalogIds
) {
  const sel = new Set(selectedWarehouseCatalogIds.map(String));
  const uuids = new Set();
  for (const w of catalogWarehouses) {
    if (!sel.has(String(w.id))) continue;
    const cid = String(w.customerId ?? '');
    const cust = catalogCustomers.find((c) => String(c.id) === cid);
    const u = String(cust?.uuid ?? '').trim();
    if (u) uuids.add(u);
  }
  return [...uuids];
}

const EMPTY_RULE_PREFERENCES = {
  hour_preferences: [],
  weekly_preferences: [],
  weekday_preferences: {},
  terminal_preferences: {},
};

/** بک‌اند: weekdayهای lu_settings / sw_settings — همه باید NONE یا EXCLUDE باشند */
const WEEKDAYS_LU_SW_API = ['sat', 'sun', 'mon', 'tue', 'wed', 'thu', 'fri'];
const DEFAULT_SW_MAX_DISTANCE = 100;
const DEFAULT_SW_MIN_EXCESS_THRESHOLD = 0;

function normalizeWeekdayPrefsMap(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out = {};
  for (const [k, v] of Object.entries(raw)) {
    const key = String(k).toLowerCase();
    if (typeof v === 'string') out[key] = v.toUpperCase();
    else if (v != null && typeof v === 'object') out[key] = v;
  }
  return out;
}

function terminalPreferencesWithPriorityFromApi(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out = {};
  for (const [key, val] of Object.entries(raw)) {
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      const priority =
        val.priority != null && val.priority !== ''
          ? String(val.priority).toUpperCase()
          : 'NONE';
      out[key] = { ...val, priority };
    }
  }
  return out;
}

function preferencesFromDefaultSettings(ds) {
  if (!ds || typeof ds !== 'object') {
    return { ...EMPTY_RULE_PREFERENCES };
  }
  return {
    hour_preferences: Array.isArray(ds.hour_preferences)
      ? [...ds.hour_preferences]
      : [],
    weekly_preferences: Array.isArray(ds.weekly_preferences)
      ? [...ds.weekly_preferences]
      : [],
    weekday_preferences: normalizeWeekdayPrefsMap(ds.weekday_preferences),
    terminal_preferences: terminalPreferencesWithPriorityFromApi(
      ds.terminal_preferences
    ),
  };
}

function mergeTerminalPreferenceObjects(baseT, overlayT) {
  const b = terminalPreferencesWithPriorityFromApi(
    baseT && typeof baseT === 'object' && !Array.isArray(baseT) ? baseT : {}
  );
  const o = terminalPreferencesWithPriorityFromApi(
    overlayT && typeof overlayT === 'object' && !Array.isArray(overlayT)
      ? overlayT
      : {}
  );
  const keys = new Set([...Object.keys(b), ...Object.keys(o)]);
  const out = {};
  for (const k of keys) {
    out[k] = { ...(b[k] || {}), ...(o[k] || {}) };
    if (out[k].priority == null || out[k].priority === '')
      out[k].priority = 'NONE';
    else out[k].priority = String(out[k].priority).toUpperCase();
  }
  return out;
}

function mergeRulePreferences(base, overlay) {
  const b = base || { ...EMPTY_RULE_PREFERENCES };
  if (!overlay || typeof overlay !== 'object') {
    return {
      hour_preferences: [...(b.hour_preferences ?? [])],
      weekly_preferences: [...(b.weekly_preferences ?? [])],
      weekday_preferences: {
        ...normalizeWeekdayPrefsMap(b.weekday_preferences),
      },
      terminal_preferences: mergeTerminalPreferenceObjects(
        b.terminal_preferences,
        {}
      ),
    };
  }
  return {
    hour_preferences:
      overlay.hour_preferences !== undefined
        ? [...overlay.hour_preferences]
        : [...(b.hour_preferences ?? [])],
    weekly_preferences:
      overlay.weekly_preferences !== undefined
        ? [...overlay.weekly_preferences]
        : [...(b.weekly_preferences ?? [])],
    weekday_preferences: {
      ...normalizeWeekdayPrefsMap(b.weekday_preferences),
      ...(overlay.weekday_preferences !== undefined
        ? normalizeWeekdayPrefsMap(overlay.weekday_preferences)
        : {}),
    },
    terminal_preferences: mergeTerminalPreferenceObjects(
      b.terminal_preferences,
      overlay.terminal_preferences !== undefined
        ? overlay.terminal_preferences
        : {}
    ),
  };
}

function sortKeysDeep(value) {
  if (value === null || value === undefined) return value;
  if (typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map(sortKeysDeep);
  const sorted = {};
  for (const k of Object.keys(value).sort()) {
    sorted[k] = sortKeysDeep(value[k]);
  }
  return sorted;
}

function serializeRulePrefsForCompare(p) {
  const weekdayRaw =
    p.weekday_preferences &&
    typeof p.weekday_preferences === 'object' &&
    !Array.isArray(p.weekday_preferences)
      ? p.weekday_preferences
      : normalizeWeekdayPrefsMap(p.weekday_preferences);
  const terminalRaw =
    p.terminal_preferences &&
    typeof p.terminal_preferences === 'object' &&
    !Array.isArray(p.terminal_preferences)
      ? p.terminal_preferences
      : {};
  return JSON.stringify({
    hour_preferences: sortKeysDeep(p.hour_preferences ?? []),
    weekly_preferences: sortKeysDeep(p.weekly_preferences ?? []),
    weekday_preferences: sortKeysDeep(weekdayRaw),
    terminal_preferences: sortKeysDeep(terminalRaw),
  });
}

function luPreferencesSourceFromRule(rule) {
  if (!rule || typeof rule !== 'object') return rule;
  const lu = rule.lu_settings;
  if (lu && typeof lu === 'object') return lu;
  return rule;
}

function preferencesOverlayFromSettingsObject(src) {
  if (!src || typeof src !== 'object') {
    return {};
  }
  const overlay = {};
  if ('hour_preferences' in src) {
    overlay.hour_preferences = Array.isArray(src.hour_preferences)
      ? [...src.hour_preferences]
      : [];
  }
  if ('weekly_preferences' in src) {
    overlay.weekly_preferences = Array.isArray(src.weekly_preferences)
      ? [...src.weekly_preferences]
      : [];
  }
  if ('weekday_preferences' in src && src.weekday_preferences != null) {
    const w = src.weekday_preferences;
    overlay.weekday_preferences =
      typeof w === 'object' && !Array.isArray(w)
        ? normalizeWeekdayPrefsMap(w)
        : {};
  }
  if ('terminal_preferences' in src && src.terminal_preferences != null) {
    overlay.terminal_preferences = terminalPreferencesWithPriorityFromApi(
      src.terminal_preferences
    );
  }
  return overlay;
}

function rulePreferencesOverlayFromRule(rule) {
  if (!rule || typeof rule !== 'object') {
    return {};
  }
  return preferencesOverlayFromSettingsObject(
    luPreferencesSourceFromRule(rule)
  );
}

/** اعتبارسنجی API: اولویت ساعت/بازه/ترمینال فقط NONE یا EXCLUDE */
function sanitizeLuSwPriorityForApi(p) {
  const u = String(p ?? '').toUpperCase();
  return u === 'EXCLUDE' ? 'EXCLUDE' : 'NONE';
}

function buildLuSwSettingsPayloadFromPreferences(prefs) {
  const wdRaw = normalizeWeekdayPrefsMap(prefs?.weekday_preferences);
  const weekday_preferences = {};
  for (const d of WEEKDAYS_LU_SW_API) {
    weekday_preferences[d] = sanitizeLuSwPriorityForApi(wdRaw[d]);
  }
  const hour_preferences = (prefs?.hour_preferences ?? []).map((row) => ({
    ...row,
    time_start: row.time_start ?? null,
    time_end: row.time_end ?? null,
    priority: sanitizeLuSwPriorityForApi(row.priority),
  }));
  const weekly_preferences = (prefs?.weekly_preferences ?? []).map((row) => ({
    ...row,
    date_start: row.date_start ?? null,
    date_end: row.date_end ?? null,
    time_start: row.time_start ?? null,
    time_end: row.time_end ?? null,
    priority: sanitizeLuSwPriorityForApi(row.priority),
  }));
  const terminalRaw =
    prefs?.terminal_preferences &&
    typeof prefs.terminal_preferences === 'object' &&
    !Array.isArray(prefs.terminal_preferences)
      ? prefs.terminal_preferences
      : {};
  const terminal_preferences = {};
  for (const [k, val] of Object.entries(terminalRaw)) {
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      terminal_preferences[k] = {
        ...val,
        priority: sanitizeLuSwPriorityForApi(val.priority),
      };
    }
  }
  return {
    hour_preferences,
    weekly_preferences,
    weekday_preferences,
    terminal_preferences,
  };
}

function minutesToTimeString(totalMin) {
  const n = Number(totalMin);
  if (!Number.isFinite(n) || n < 0) return '';
  const h = Math.floor(n / 60);
  const m = Math.round(n % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function liveUnloadFieldsFromRule(rule) {
  if (!rule || typeof rule !== 'object') {
    return {
      time: '',
      driveMin: '',
      backwardWindowHour: '',
      forwardWindowHour: '',
    };
  }
  const timeRaw =
    rule.lu_unload_time ??
    (rule.lu_unload_duration != null && rule.lu_unload_duration !== ''
      ? minutesToTimeString(rule.lu_unload_duration)
      : '');
  const driveMin =
    rule.lu_drive_to_port_min ??
    '';
  const backwardWindowHour =
    rule.lu_offset_duration_bw ??
    '';
  const forwardWindowHour =
    rule.lu_offset_duration_fw ??
    '';
  return {
    time: timeRaw != null && timeRaw !== '' ? String(timeRaw) : '',
    driveMin: driveMin != null && driveMin !== '' ? String(driveMin) : '',
    backwardWindowHour:
      backwardWindowHour != null && backwardWindowHour !== ''
        ? String(backwardWindowHour)
        : '',
    forwardWindowHour:
      forwardWindowHour != null && forwardWindowHour !== ''
        ? String(forwardWindowHour)
        : '',
  };
}

function timeStringToTotalMinutes(s) {
  if (s == null || String(s).trim() === '') return NaN;
  const parts = String(s).trim().split(':');
  if (parts.length < 2) return NaN;
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return NaN;
  return h * 60 + m;
}

export const AutobookRules = () => {
  const [ruleModal, setRuleModal] = useState({
    show: false,
    mode: 'add',
    rule: null,
  });
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [deletingUuid, setDeletingUuid] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState({
    show: false,
    data: null,
  });
  const [rulesData, setRulesData] = useState([]);
  const [ruleName, setRuleName] = useState('');
  const [priority, setPriority] = useState('');
  const [ruleActive, setRuleActive] = useState(false);
  const [autoEnableAutobook, setAutoEnableAutobook] = useState(false);
  const [draftRuleUuid, setDraftRuleUuid] = useState(null);
  const [isDraftLoading, setIsDraftLoading] = useState(false);
  const [submittingRule, setSubmittingRule] = useState(false);
  const [deliveryTypeOptionsList, setDeliveryTypeOptionsList] = useState(() =>
    buildDeliveryTypeOptions(null)
  );
  const [deliveryTypeOption, setDeliveryTypeOption] = useState(
    () => buildDeliveryTypeOptions(null)[0]
  );
  const [maximumDaysAfterWindow, setMaximumDaysAfterWindow] = useState('');
  const [liveUnloadTime, setLiveUnloadTime] = useState('');
  const [liveUnloadDriveToPortMin, setLiveUnloadDriveToPortMin] = useState('');
  const [
    liveUnloadAutobookBackwardWindowHour,
    setLiveUnloadAutobookBackwardWindowHour,
  ] = useState('');
  const [
    liveUnloadAutobookForwardWindowHour,
    setLiveUnloadAutobookForwardWindowHour,
  ] = useState('');
  const [draftCustomers, setDraftCustomers] = useState([]);
  const [draftWarehouses, setDraftWarehouses] = useState([]);
  const [draftWarehouseGroups, setDraftWarehouseGroups] = useState([]);
  const [selectedWarehouseCatalogIds, setSelectedWarehouseCatalogIds] =
    useState([]);
  const [selectedGroupCatalogIds, setSelectedGroupCatalogIds] = useState([]);
  const [ruleScopeCustomerUuids, setRuleScopeCustomerUuids] = useState([]);
  const [rulePreferences, setRulePreferences] = useState(
    EMPTY_RULE_PREFERENCES
  );
  const [swRulePreferences, setSwRulePreferences] = useState(
    EMPTY_RULE_PREFERENCES
  );
  const [swMaxDistance, setSwMaxDistance] = useState(
    String(DEFAULT_SW_MAX_DISTANCE)
  );
  const [swMinExcessThreshold, setSwMinExcessThreshold] = useState(
    String(DEFAULT_SW_MIN_EXCESS_THRESHOLD)
  );
  const [swBeforeTargerReturn, setSwBeforeTargerReturn] = useState('');
  const [swAfterTargerReturn, setSwAfterTargerReturn] = useState('');
  const [swSameGroup, setSwSameGroup] = useState(false);
  const [swSameCity, setSwSameCity] = useState(false);
  const [swUseOldestEmptyFirst, setSwUseOldestEmptyFirst] = useState(false);
  const [
    selectedSwSharingGroupCatalogIds,
    setSelectedSwSharingGroupCatalogIds,
  ] = useState([]);
  const [ruleStatic, setRuleStatic] = useState(false);

  const onRulePreferencesChange = useCallback((next) => {
    const merged = {
      hour_preferences: next.hour_preferences ?? [],
      weekly_preferences: next.weekly_preferences ?? [],
      weekday_preferences:
        next.weekday_preferences &&
        typeof next.weekday_preferences === 'object' &&
        !Array.isArray(next.weekday_preferences)
          ? next.weekday_preferences
          : normalizeWeekdayPrefsMap(next.weekday_preferences),
      terminal_preferences: next.terminal_preferences ?? {},
    };
    setRulePreferences((prev) =>
      serializeRulePrefsForCompare(prev) ===
      serializeRulePrefsForCompare(merged)
        ? prev
        : merged
    );
  }, []);

  const onSwRulePreferencesChange = useCallback((next) => {
    const merged = {
      hour_preferences: next.hour_preferences ?? [],
      weekly_preferences: next.weekly_preferences ?? [],
      weekday_preferences:
        next.weekday_preferences &&
        typeof next.weekday_preferences === 'object' &&
        !Array.isArray(next.weekday_preferences)
          ? next.weekday_preferences
          : normalizeWeekdayPrefsMap(next.weekday_preferences),
      terminal_preferences: next.terminal_preferences ?? {},
    };
    setSwRulePreferences((prev) =>
      serializeRulePrefsForCompare(prev) ===
      serializeRulePrefsForCompare(merged)
        ? prev
        : merged
    );
  }, []);

  const preferencesSyncKey = `${ruleModal.mode}-${
    draftRuleUuid || ruleModal.rule?.uuid || 'pending'
  }`;

  const hydrateFromCreateOrGetBody = useCallback((body) => {
    const inner = body?.data ?? body;
    const rule = inner?.rule;
    const uuid =
      rule?.uuid ??
      inner?.uuid ??
      body?.data?.uuid ??
      body?.rule?.uuid ??
      body?.uuid;
    const customersFromData = Array.isArray(inner?.customers)
      ? inner.customers
      : [];
    const warehousesFromData = Array.isArray(inner?.warehouses)
      ? inner.warehouses
      : [];
    const groupsFromData = Array.isArray(inner?.warehouseGroups)
      ? inner.warehouseGroups
      : Array.isArray(inner?.warehouse_groups)
      ? inner.warehouse_groups
      : [];
    const customersList =
      customersFromData.length > 0
        ? customersFromData
        : Array.isArray(rule?.customers)
        ? rule.customers
        : [];
    const warehousesList =
      warehousesFromData.length > 0
        ? warehousesFromData
        : Array.isArray(rule?.warehouses)
        ? rule.warehouses
        : [];
    const groupsRaw =
      groupsFromData.length > 0
        ? groupsFromData
        : Array.isArray(rule?.warehouse_groups)
        ? rule.warehouse_groups
        : Array.isArray(rule?.warehouseGroups)
        ? rule.warehouseGroups
        : [];
    const customers = normalizeDraftCustomers(customersList);
    const warehouses = normalizeDraftWarehouses(warehousesList);
    const warehouseGroups = normalizeDraftWarehouseGroups(
      Array.isArray(groupsRaw) ? groupsRaw : []
    );
    const typeOpts = buildDeliveryTypeOptions(
      getDeliveryTypesObjectFromResponse(inner, body)
    );
    setDraftCustomers(customers);
    setDraftWarehouses(warehouses);
    setDraftWarehouseGroups(warehouseGroups);
    setDeliveryTypeOptionsList(typeOpts);

    if (rule) {
      const isStatic = !!rule.static;
      setRuleStatic(isStatic);
      if (isStatic) {
        setSelectedWarehouseCatalogIds(warehouses.map((w) => String(w.id)));
      } else {
        setSelectedWarehouseCatalogIds(
          catalogIdsMatchingRuleItems(warehouses, rule.warehouses ?? [])
        );
      }
      setSelectedGroupCatalogIds(
        catalogIdsMatchingRuleItems(
          warehouseGroups,
          rule.warehouse_groups ?? rule.warehouseGroups ?? []
        )
      );
      setRuleScopeCustomerUuids(
        isStatic
          ? customers
              .map((c) => String(c.uuid ?? '').trim())
              .filter(Boolean)
          : catalogUuidsMatchingRuleItems(customers, rule.customers ?? [])
      );
    } else {
      setRuleStatic(false);
      setSelectedWarehouseCatalogIds([]);
      setSelectedGroupCatalogIds([]);
      setRuleScopeCustomerUuids([]);
    }

    const sharingGroupsRaw = sharingWarehouseGroupsFromApi(inner, rule);
    setSelectedSwSharingGroupCatalogIds(
      catalogIdsMatchingRuleItems(warehouseGroups, sharingGroupsRaw)
    );

    const ds = inner?.defaultSettings ?? inner?.default_settings;
    const basePrefs = preferencesFromDefaultSettings(ds);

    if (rule) {
      setRuleName(rule.name ?? '');
      setPriority(rule.priority != null ? String(rule.priority) : '');
      setRuleActive(!!rule.active);
      setAutoEnableAutobook(!!rule.auto_enable);
      setDeliveryTypeOption(
        deliveryTypeOptionFromRuleValue(rule.delivery_type, typeOpts)
      );
      setMaximumDaysAfterWindow(
        rule.maximum_days_after != null ? String(rule.maximum_days_after) : ''
      );
      const lu = liveUnloadFieldsFromRule(rule);
      setLiveUnloadTime(lu.time);
      setLiveUnloadDriveToPortMin(lu.driveMin);
      setLiveUnloadAutobookBackwardWindowHour(lu.backwardWindowHour);
      setLiveUnloadAutobookForwardWindowHour(lu.forwardWindowHour);
      setSwMaxDistance(
        rule.sw_max_distance != null
          ? String(rule.sw_max_distance)
          : String(DEFAULT_SW_MAX_DISTANCE)
      );
      setSwMinExcessThreshold(
        rule.sw_min_excess_threshold != null
          ? String(rule.sw_min_excess_threshold)
          : String(DEFAULT_SW_MIN_EXCESS_THRESHOLD)
      );
      setSwBeforeTargerReturn(
        rule.sw_before_targer_return != null
          ? String(rule.sw_before_targer_return)
          : ''
      );
      setSwAfterTargerReturn(
        rule.sw_after_targer_return != null
          ? String(rule.sw_after_targer_return)
          : ''
      );
      setSwSameGroup(!!rule.sw_same_group);
      setSwSameCity(!!rule.sw_same_city);
      setSwUseOldestEmptyFirst(!!rule.sw_use_oldest_empty_first);
      const ruleOverlay = rulePreferencesOverlayFromRule(rule);
      setRulePreferences(mergeRulePreferences(basePrefs, ruleOverlay));
      const swSrc =
        rule.sw_settings && typeof rule.sw_settings === 'object'
          ? rule.sw_settings
          : null;
      const swOverlayFromApi = preferencesOverlayFromSettingsObject(swSrc);
      const useSwFromApi = swSrc != null && Object.keys(swSrc).length > 0;
      setSwRulePreferences(
        mergeRulePreferences(basePrefs, useSwFromApi ? swOverlayFromApi : {})
      );
    } else {
      setSwMaxDistance(String(DEFAULT_SW_MAX_DISTANCE));
      setSwMinExcessThreshold(String(DEFAULT_SW_MIN_EXCESS_THRESHOLD));
      setSwBeforeTargerReturn('');
      setSwAfterTargerReturn('');
      setSwSameGroup(false);
      setSwSameCity(false);
      setSwUseOldestEmptyFirst(false);
      const emptyOrBase = ds ? basePrefs : { ...EMPTY_RULE_PREFERENCES };
      setRulePreferences(emptyOrBase);
      setSwRulePreferences(mergeRulePreferences(emptyOrBase, {}));
    }
    return uuid;
  }, []);

  useEffect(() => {
    if (!ruleModal.show || !ruleStatic) return;
    if (!Array.isArray(draftWarehouses) || draftWarehouses.length === 0) return;
    const allIds = draftWarehouses.map((w) => String(w.id));
    setSelectedWarehouseCatalogIds((prev) => {
      if (
        prev.length === allIds.length &&
        allIds.every((id) => prev.includes(id))
      ) {
        return prev;
      }
      return allIds;
    });
  }, [ruleModal.show, ruleStatic, draftWarehouses]);

  useEffect(() => {
    if (!ruleModal.show || !ruleStatic) return;
    if (!Array.isArray(draftCustomers) || draftCustomers.length === 0) return;
    const allUuids = draftCustomers
      .map((c) => String(c.uuid ?? '').trim())
      .filter(Boolean);
    setRuleScopeCustomerUuids((prev) => {
      if (
        prev.length === allUuids.length &&
        allUuids.every((u) => prev.includes(u))
      ) {
        return prev;
      }
      return allUuids;
    });
  }, [ruleModal.show, ruleStatic, draftCustomers]);

  const loadRules = () => {
    setIsLoadingData(true);
    getAutobookRules()
      .then((res) => {
        const list =
          res?.data?.rules ||
          res?.data?.data?.rules ||
          res?.data?.list ||
          res?.data?.data?.list ||
          res?.data?.autobook_rules ||
          res?.data?.data?.autobook_rules ||
          [];
        setRulesData(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        setRulesData([]);
      })
      .finally(() => {
        setIsLoadingData(false);
      });
  };

  useEffect(() => {
    loadRules();
  }, []);

  useEffect(() => {
    if (!ruleModal.show || ruleModal.mode !== 'edit' || !ruleModal.rule?.uuid) {
      return;
    }
    const editUuid = ruleModal.rule.uuid;
    let cancelled = false;
    setIsDraftLoading(true);
    createOrGetAutobookRule(editUuid)
      .then((body) => {
        if (cancelled) return;
        hydrateFromCreateOrGetBody(body);
      })
      .catch((err) => {
        if (!cancelled) {
          SaveApiResMessage({
            title: 'Error',
            message: err,
            mode: 'error',
          });
        }
      })
      .finally(() => {
        if (!cancelled) setIsDraftLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    ruleModal.show,
    ruleModal.mode,
    ruleModal.rule?.uuid,
    hydrateFromCreateOrGetBody,
  ]);

  useEffect(() => {
    if (!ruleModal.show || ruleModal.mode !== 'add') {
      setDraftRuleUuid(null);
      if (!ruleModal.show) {
        setIsDraftLoading(false);
      }
      return;
    }
    let cancelled = false;
    setIsDraftLoading(true);
    setDraftRuleUuid(null);
    createOrGetAutobookRule()
      .then((body) => {
        if (cancelled) return;
        const uuid = hydrateFromCreateOrGetBody(body);
        if (!uuid) {
          SaveApiResMessage({
            title: 'Error',
            message: 'Could not start a new rule. Missing uuid in response.',
            mode: 'error',
          });
          return;
        }
        setDraftRuleUuid(uuid);
      })
      .catch((err) => {
        if (!cancelled) {
          SaveApiResMessage({
            title: 'Error',
            message: err,
            mode: 'error',
          });
        }
      })
      .finally(() => {
        if (!cancelled) setIsDraftLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [ruleModal.show, ruleModal.mode, hydrateFromCreateOrGetBody]);

  const handleDeleteRule = (row) => {
    if (!row?.uuid || deletingUuid) return;
    setDeletingUuid(row.uuid);
    deleteAutobookRule(row.uuid)
      .then((res) => {
        SaveApiResMessage({
          title: res?.message || 'Success',
          message: 'Autobook rule deleted successfully',
          mode: 'success',
        });
        loadRules();
      })
      .catch((err) => {
        SaveApiResMessage({
          title: 'Error',
          message: err,
          mode: 'error',
        });
      })
      .finally(() => {
        setDeletingUuid(null);
        setShowDeleteModal({ show: false, data: null });
      });
  };

  const closeRuleModal = () => {
    setDraftRuleUuid(null);
    setIsDraftLoading(false);
    setSubmittingRule(false);
    setDraftCustomers([]);
    setDraftWarehouses([]);
    setDraftWarehouseGroups([]);
    setSelectedWarehouseCatalogIds([]);
    setSelectedGroupCatalogIds([]);
    setSelectedSwSharingGroupCatalogIds([]);
    setRuleScopeCustomerUuids([]);
    const opts = buildDeliveryTypeOptions(null);
    setDeliveryTypeOptionsList(opts);
    setDeliveryTypeOption(opts[0]);
    setRuleName('');
    setPriority('');
    setRuleActive(false);
    setAutoEnableAutobook(false);
    setMaximumDaysAfterWindow('');
    setLiveUnloadTime('');
    setLiveUnloadDriveToPortMin('');
    setLiveUnloadAutobookBackwardWindowHour('');
    setLiveUnloadAutobookForwardWindowHour('');
    setRulePreferences({ ...EMPTY_RULE_PREFERENCES });
    setSwRulePreferences({ ...EMPTY_RULE_PREFERENCES });
    setSwMaxDistance(String(DEFAULT_SW_MAX_DISTANCE));
    setSwMinExcessThreshold(String(DEFAULT_SW_MIN_EXCESS_THRESHOLD));
    setSwBeforeTargerReturn('');
    setSwAfterTargerReturn('');
    setSwSameGroup(false);
    setSwSameCity(false);
    setSwUseOldestEmptyFirst(false);
    setRuleStatic(false);
    setRuleModal({ show: false, mode: 'add', rule: null });
    loadRules();
  };

  const handleRuleModalSubmit = () => {
    const uuid =
      ruleModal.mode === 'add' ? draftRuleUuid : ruleModal.rule?.uuid;
    if (!uuid) {
      SaveApiResMessage({
        title: 'Error',
        message:
          ruleModal.mode === 'add'
            ? 'Please wait until the new rule is ready, then try again.'
            : 'Missing rule identifier.',
        mode: 'error',
      });
      return;
    }
    const p = Number.parseInt(String(priority), 10);
    const maxDays = Number.parseInt(String(maximumDaysAfterWindow), 10);
    const luDrive = Number.parseInt(String(liveUnloadDriveToPortMin), 10);
    const luWinBack = Number.parseInt(
      String(liveUnloadAutobookBackwardWindowHour),
      10
    );
    const luWinFwd = Number.parseInt(
      String(liveUnloadAutobookForwardWindowHour),
      10
    );
    const unloadMinutes = timeStringToTotalMinutes(liveUnloadTime.trim());
    const swMax = Number.parseInt(String(swMaxDistance), 10);
    const swMin = Number.parseInt(String(swMinExcessThreshold), 10);
    const swBeforeTarget = Number.parseInt(String(swBeforeTargerReturn), 10);
    const swAfterTarget = Number.parseInt(String(swAfterTargerReturn), 10);
    const luUnloadDur =
      Number.isFinite(unloadMinutes) && unloadMinutes > 0 ? unloadMinutes : 1;
    const luOffsetDur = Number.isFinite(luDrive) && luDrive > 0 ? luDrive : 1;
    const maxDaysAfter = Number.isFinite(maxDays) && maxDays > 0 ? maxDays : 1;
    const priorityVal = Number.isFinite(p) && p > 0 ? p : 1;
    const whSel = new Set(selectedWarehouseCatalogIds.map(String));
    const grpSel = new Set(selectedGroupCatalogIds.map(String));
    const swSharingSel = new Set(selectedSwSharingGroupCatalogIds.map(String));
    const warehouseUuids = draftWarehouses
      .filter((w) => whSel.has(String(w.id)))
      .map((w) => String(w.uuid ?? '').trim())
      .filter(Boolean);
    const warehouseGroupUuids = draftWarehouseGroups
      .filter((g) => grpSel.has(String(g.id)))
      .map((g) => String(g.uuid ?? g.id ?? '').trim())
      .filter(Boolean);
    const swSharingWarehouseGroupUuids = draftWarehouseGroups
      .filter((g) => swSharingSel.has(String(g.id)))
      .map((g) => String(g.uuid ?? g.id ?? '').trim())
      .filter(Boolean);
    const fromWarehouses = customerUuidsFromWarehouseSelection(
      draftCustomers,
      draftWarehouses,
      selectedWarehouseCatalogIds
    );
    const customerUuids = [
      ...new Set([...fromWarehouses, ...ruleScopeCustomerUuids]),
    ];
    const lu_settings =
      buildLuSwSettingsPayloadFromPreferences(rulePreferences);
    const sw_settings =
      buildLuSwSettingsPayloadFromPreferences(swRulePreferences);
    const payload = {
      name: ruleName.trim(),
      priority: priorityVal,
      active: ruleActive,
      auto_enable: autoEnableAutobook,
      delivery_type: 'ALL',
      maximum_days_after: maxDaysAfter,
      customers: customerUuids,
      warehouses: warehouseUuids,
      warehouseGroups: warehouseGroupUuids,
      lu_unload_time: liveUnloadTime.trim(),
      lu_drive_to_port_min: luOffsetDur,
      lu_autobook_window_hour: Number.isFinite(luWinBack) ? luWinBack : 0,
      lu_autobook_forward_window_hour: Number.isFinite(luWinFwd) ? luWinFwd : 0,
      lu_unload_duration: luUnloadDur,
      lu_offset_duration: luOffsetDur,
      lu_offset_duration_bw: Number.isFinite(luWinBack) ? luWinBack : 0,
      lu_offset_duration_fw: Number.isFinite(luWinFwd) ? luWinFwd : 0,
      sw_max_distance:
        Number.isFinite(swMax) && swMax > 15 ? swMax : DEFAULT_SW_MAX_DISTANCE,
      sw_min_excess_threshold:
        Number.isFinite(swMin) && swMin >= 0
          ? swMin
          : DEFAULT_SW_MIN_EXCESS_THRESHOLD,
      sw_same_group: !!swSameGroup,
      sw_same_city: !!swSameCity,
      sw_use_oldest_empty_first: !!swUseOldestEmptyFirst,
      sw_before_targer_return: Number.isFinite(swBeforeTarget)
        ? swBeforeTarget
        : null,
      sw_after_targer_return: Number.isFinite(swAfterTarget)
        ? swAfterTarget
        : null,
      swSharingWarehouseGroups: swSharingWarehouseGroupUuids,
      lu_settings,
      sw_settings,
    };
    setSubmittingRule(true);
    patchAutobookRule(uuid, payload)
      .then((res) => {
        SaveApiResMessage({
          title: res?.message || 'Success',
          message:
            ruleModal.mode === 'add'
              ? 'Rule saved successfully'
              : 'Rule updated successfully',
          mode: 'success',
        });
        closeRuleModal();
      })
      .catch((err) => {
        SaveApiResMessage({
          title: 'Error',
          message: err,
          mode: 'error',
        });
      })
      .finally(() => setSubmittingRule(false));
  };

  const columns = useMemo(
    () => [
      {
        field: 'name',
        label: 'Rule Name',
      },
      {
        field: 'static',
        label: 'Scope',
        component: (row) => (row.static ? 'Global' : 'Custom'),
      },
      {
        field: 'delivery_type',
        label: 'Delivery Type',
      },
      {
        field: 'rule_status',
        label: 'Rule Status',
        component: (row) => (row.active ? 'Active' : 'Inactive'),
      },
      {
        field: 'auto_enable',
        label: 'Autobook',
        component: (row) => (row.auto_enable ? 'ON' : 'OFF'),
      },
      {
        field: 'lu_unload_duration',
        label: 'Unloading Time (Hrs)',
        component: (row) =>
          row.lu_unload_duration ? `${row.lu_unload_duration / 60} Hrs` : '-',
      },
      {
        field: 'sw_min_excess_threshold',
        label: 'Buffer Time (Hrs)',
        component: (row) =>
          row.sw_min_excess_threshold != null
            ? `${row.sw_min_excess_threshold} Hrs`
            : '-',
      },
      {
        field: 'sw_same_group',
        label: 'Groups',
        component: (row) => (row.sw_same_group ? 'Same Group' : '-'),
      },
      {
        field: 'active',
        label: 'Global Prefs',
        component: (row) => (row.priority ? row.priority : '-'),
      },
      {
        field: 'actions',
        label: 'Actions',
        align: 'center',
        flex: 1,
        component: (row) => (
          <div className="flex gap-2 items-center justify-center">
            <button
              onClick={() => {
                setIsDraftLoading(true);
                setRuleModal({ show: true, mode: 'edit', rule: row });
              }}
              className="p-2 bg-transparent shadow-none cursor-pointer border-solid rounded border border-border-light-secondary hover:bg-gray-100"
              title="Edit">
              <Icon src={Icons.editIcon} width={16} height={16} />
            </button>
            <button
              onClick={() => setShowDeleteModal({ show: true, data: row })}
              disabled={deletingUuid === row.uuid}
              className="p-2 bg-transparent shadow-none cursor-pointer border-solid rounded border border-error-light hover:bg-gray-100"
              title="Delete">
              <Icon
                color="#D02533"
                src={Icons.trashIcon}
                width={16}
                height={16}
              />
            </button>
          </div>
        ),
        sortValue: () => '',
      },
    ],
    [deletingUuid]
  );

  return (
    <div className="pb-6">
      <div className="mt-6 bg-white">
        <div className="flex items-center justify-between gap-4 mb-2">
          <h1 className="text-lg font-bold text-primary-text m-0">
            Autobook Rules
          </h1>
          <IconButton
            text="Add Rule"
            onClick={() => {
              setIsDraftLoading(true);
              setRuleModal({ show: true, mode: 'add', rule: null });
            }}
            icon="Add"
            color="white"
            style={{
              height: 40,
              borderRadius: 4,
              width: 140,
              backgroundColor: '#131517',
              color: 'white',
              cursor: 'pointer',
              borderColor: '#131517',
            }}
          />
        </div>

        <p className="text-secondary-text text-base m-0 mb-6">
          Create or update rules to control when, where, and how AutoBook
          schedules your appointments.
        </p>
      </div>
      <div>
        {isLoadingData ? (
          <LoadingV2 />
        ) : (
          <TableV3
            headers={columns}
            data={rulesData}
            emptyMessage=""
            minWidth="1244px"
            stickyHeader={true}
            appHeaderHeight={64}
          />
        )}
      </div>
      <Modal
        show={ruleModal.show}
        setShow={(open) => {
          if (!open) closeRuleModal();
        }}
        preventBackdropClose
        modalWidth={1320}
        maxHeightModal={935}
        icon="saveData"
        titleColor="#131517"
        title={ruleModal.mode === 'add' ? 'Add Rule' : 'Edit Rule'}>
        <AutobookRuleFormBody
          mode={ruleModal.mode}
          onSubmit={handleRuleModalSubmit}
          submitLoading={submittingRule}
          draftLoading={isDraftLoading}
          submitDisabled={
            (ruleModal.mode === 'add' && (!draftRuleUuid || isDraftLoading)) ||
            (ruleModal.mode === 'edit' && isDraftLoading)
          }
          ruleName={ruleName}
          setRuleName={setRuleName}
          priority={priority}
          setPriority={setPriority}
          ruleActive={ruleActive}
          setRuleActive={setRuleActive}
          autoEnableAutobook={autoEnableAutobook}
          setAutoEnableAutobook={setAutoEnableAutobook}
          deliveryTypeOption={deliveryTypeOption}
          setDeliveryTypeOption={setDeliveryTypeOption}
          maximumDaysAfterWindow={maximumDaysAfterWindow}
          setMaximumDaysAfterWindow={setMaximumDaysAfterWindow}
          deliveryTypeOptions={deliveryTypeOptionsList}
          draftCustomers={draftCustomers}
          draftWarehouses={draftWarehouses}
          draftWarehouseGroups={draftWarehouseGroups}
          selectedWarehouseCatalogIds={selectedWarehouseCatalogIds}
          setSelectedWarehouseCatalogIds={setSelectedWarehouseCatalogIds}
          selectedGroupCatalogIds={selectedGroupCatalogIds}
          setSelectedGroupCatalogIds={setSelectedGroupCatalogIds}
          draftRuleUuid={draftRuleUuid}
          liveUnloadTime={liveUnloadTime}
          setLiveUnloadTime={setLiveUnloadTime}
          liveUnloadDriveToPortMin={liveUnloadDriveToPortMin}
          setLiveUnloadDriveToPortMin={setLiveUnloadDriveToPortMin}
          liveUnloadAutobookBackwardWindowHour={
            liveUnloadAutobookBackwardWindowHour
          }
          setLiveUnloadAutobookBackwardWindowHour={
            setLiveUnloadAutobookBackwardWindowHour
          }
          liveUnloadAutobookForwardWindowHour={
            liveUnloadAutobookForwardWindowHour
          }
          setLiveUnloadAutobookForwardWindowHour={
            setLiveUnloadAutobookForwardWindowHour
          }
          rulePreferences={rulePreferences}
          onRulePreferencesChange={onRulePreferencesChange}
          swRulePreferences={swRulePreferences}
          onSwRulePreferencesChange={onSwRulePreferencesChange}
          preferencesSyncKey={preferencesSyncKey}
          swUseOldestEmptyFirst={swUseOldestEmptyFirst}
          setSwUseOldestEmptyFirst={setSwUseOldestEmptyFirst}
          swSameGroup={swSameGroup}
          setSwSameGroup={setSwSameGroup}
          swSameCity={swSameCity}
          setSwSameCity={setSwSameCity}
          swMaxDistance={swMaxDistance}
          setSwMaxDistance={setSwMaxDistance}
          swMinExcessThreshold={swMinExcessThreshold}
          setSwMinExcessThreshold={setSwMinExcessThreshold}
          swBeforeTargerReturn={swBeforeTargerReturn}
          setSwBeforeTargerReturn={setSwBeforeTargerReturn}
          swAfterTargerReturn={swAfterTargerReturn}
          setSwAfterTargerReturn={setSwAfterTargerReturn}
          selectedSwSharingGroupCatalogIds={selectedSwSharingGroupCatalogIds}
          setSelectedSwSharingGroupCatalogIds={
            setSelectedSwSharingGroupCatalogIds
          }
          scopeSelectionLocked={ruleStatic}
        />
      </Modal>
      <Modal
        show={showDeleteModal.show}
        setShow={() => setShowDeleteModal({ show: false, data: null })}
        submit={() => handleDeleteRule(showDeleteModal.data)}
        submitText="Yes, Delete"
        loadingbutton={deletingUuid === showDeleteModal.data?.uuid}
        icon="deleteIcon"
        title="Delete Rule">
        <div className="flex flex-col items-center justify-center gap-4 w-full py-2">
          <span className="text-primary-text font-bold">
            Are you sure you want to delete this rule?
          </span>
          <span className="text-secondary-text max-w-480 px-10 mx-auto text-center">
            Once deleted, this autobook rule will be removed permanently.
          </span>
        </div>
      </Modal>
    </div>
  );
};
